'use client';

import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import dayjs, { Dayjs } from 'dayjs';

import { useWindowDimensions } from '@/hooks/use-window-dimensions';
import { useTranslation } from '@/i18n/client';
import { selectEventsLoading } from '@/store/calendar-events/calendar-events.selectors';
import { selectRulerDateRange } from '@/store/calendar-navigation';
import { WithLang } from '@/types/general';
import { dates, screenBreakpoints } from '@/utils/constants';

import { useCalendar } from './use-calendar';

interface RulerCellProps {
    day: Dayjs;
    onClick: () => void;
    isSelected: boolean;
    disabled?: boolean;
    className?: string;
    isDayOff: (day: Dayjs) => boolean;
    id: string;
}

const RulerCell = (props: WithLang<RulerCellProps>) => {
    const { t } = useTranslation(props.lang, 'calendar');
    // eslint-disable-next-line unicorn/consistent-function-scoping
    const isDayOffClass = (day: Dayjs) => (props.isDayOff(day) ? 'bg-ruler_accent bg-cover text-white' : '');
    // eslint-disable-next-line unicorn/consistent-function-scoping
    const isTodayClass = (day: Dayjs) =>
        day.isSame(dates.today) ? 'border-accent rounded border-accent' : 'border-transparent';
    // eslint-disable-next-line unicorn/consistent-function-scoping
    const selectedDayClass = props.isSelected ? '!bg-accent' : '';

    const loading = useSelector(selectEventsLoading);

    return (
        <button
            id={props.id}
            disabled={loading}
            onClick={props.onClick}
            className={`flex w-10 cursor-pointer flex-col items-center border px-[10px] py-1 transition-all duration-200 disabled:cursor-default disabled:opacity-50 ${selectedDayClass} ${isTodayClass(props.day)} ${props.className}`}
        >
            {props.day.date()}
            <div className={`flex items-center rounded-sm px-1 text-[10px] uppercase ${isDayOffClass(props.day)} `}>
                {t('weekdays.short', { returnObjects: true })[props.day.day()]}
            </div>
        </button>
    );
};

interface CalendarRulerProps {
    selectedDays: Array<Dayjs>;
    onSelectDays: (day: Dayjs) => void;
    disabled?: boolean;
    isHidden: boolean;
    todayHandler: () => void;
}

export default function CalendarRuler(props: WithLang<CalendarRulerProps>) {
    const { t: tCa } = useTranslation(props.lang, 'calendar');
    const { createDaysArray, checkIsDayOff } = useCalendar();

    const { dimensions } = useWindowDimensions();

    const rulerDateRange = useSelector(selectRulerDateRange);

    const rulerRef = useRef<HTMLDivElement>(null);
    const rulerClientWidth = rulerRef.current?.clientWidth || 0;
    const rulerScrollWidth = rulerRef.current?.scrollWidth || 0;

    const cellWidth = 40;
    const scrollTwoWeeksWidth = cellWidth * 14;
    const scrollWeekWidth = cellWidth * 7;

    const isInRangeClass = (day: Dayjs) =>
        day.isAfter(props.selectedDays[0]) && day.isBefore(props.selectedDays[1]) ? 'bg-accent bg-opacity-30' : '';

    const isDaySelected = (day: Dayjs) =>
        day.utc().format(dates.format) === rulerDateRange[0] ||
        day.utc().endOf('day').format(dates.format) === rulerDateRange[1];

    const createRulerDaysArray = () => {
        let monthArray = [];
        const todayDay = dates.today.date();
        const daysInCurrentMonth = dates.today.daysInMonth();
        const remainingDaysInMonth = daysInCurrentMonth - todayDay + 1;

        const firstMonthDays = createDaysArray(dates.today, remainingDaysInMonth, 'D');
        monthArray.push(firstMonthDays);

        const monthCount = window.innerWidth >= screenBreakpoints.sm ? 4 : 2;

        for (let i = 1; i < monthCount; i++) {
            monthArray.push(createDaysArray(dates.today.add(i, 'M'), dates.today.add(i, 'M').daysInMonth(), 'M'));
        }

        return monthArray;
    };

    const rulerDays = createRulerDaysArray();
    const [scrollPosition, setScrollPosition] = useState(0);

    const handleScrollToCell = (id: string, behavior: ScrollBehavior = 'smooth') => {
        // eslint-disable-next-line unicorn/prefer-query-selector
        const el = document.getElementById(id);
        const lefOffset = el?.offsetLeft && el?.offsetLeft - 80;
        rulerRef.current?.scrollTo({ left: lefOffset, behavior: behavior });
        lefOffset && setScrollPosition(lefOffset);
    };

    const ScrollButtons = () => {
        if (dimensions.width <= screenBreakpoints.sm) return;

        const getScrollValues = () => {
            switch (true) {
                case dimensions.width > screenBreakpoints.xl:
                    return scrollTwoWeeksWidth;
                case dimensions.width > screenBreakpoints.sm:
                    return scrollWeekWidth;
                default:
                    return rulerClientWidth - cellWidth * 2;
            }
        };

        const checkMaxLimit = rulerClientWidth > rulerScrollWidth - (scrollPosition + cellWidth);
        const checkMinScrollLimit = scrollPosition < rulerClientWidth;

        const checkMinScrolLimit = () => {
            return scrollPosition <= 0;
        };

        const checkMaxScrollLimit = () => {
            return rulerScrollWidth - scrollPosition <= rulerClientWidth;
        };

        const handleScrollBack = () => {
            checkMinScrollLimit ? setScrollPosition(0) : setScrollPosition(scrollPosition - getScrollValues());
        };

        const handleScrollNext = () => {
            checkMaxLimit
                ? setScrollPosition(rulerScrollWidth - rulerClientWidth)
                : setScrollPosition(scrollPosition + getScrollValues());
        };

        return (
            <div className="relative flex w-full justify-between">
                <div className="absolute -bottom-[73px] left-0 z-[1] hidden h-14 w-14 items-center justify-start bg-gradient-to-l from-transparent to-white to-50% md:flex md:w-20">
                    <button
                        aria-label="Scroll back"
                        disabled={checkMinScrolLimit()}
                        onClick={handleScrollBack}
                        className="border-regular h-8 w-8 rotate-90 cursor-pointer rounded-full bg-white bg-extend bg-center bg-no-repeat disabled:opacity-30"
                    />
                </div>
                <div className="absolute -bottom-[73px] right-0 z-[1] hidden h-14 w-14 items-center justify-end bg-gradient-to-r from-transparent to-white to-50% md:flex md:w-20">
                    <button
                        aria-label="Scroll next"
                        onClick={handleScrollNext}
                        disabled={checkMaxScrollLimit()}
                        className="border-regular h-8 w-8 -rotate-90 cursor-pointer rounded-full bg-white bg-extend bg-center bg-no-repeat disabled:opacity-30"
                    />
                </div>
            </div>
        );
    };

    useEffect(() => {
        rulerRef.current?.scrollTo({ left: scrollPosition, behavior: 'smooth' });
    }, [scrollPosition]);

    useEffect(() => {
        const firstRulerMonth = rulerDays[0];
        const lastRulerMonth = rulerDays[rulerDays.length - 1];
        if (dayjs(rulerDateRange[0]).isBefore(firstRulerMonth[0])) {
            setScrollPosition(0);
            return;
        }

        if (dayjs(rulerDateRange[1]).isAfter(lastRulerMonth[lastRulerMonth.length - 1])) {
            setScrollPosition(rulerScrollWidth - rulerClientWidth);
            return;
        }

        if (dayjs(rulerDateRange[0]).isSame(dayjs(rulerDateRange[1]))) {
            handleScrollToCell(rulerDateRange[0]);
        }
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rulerDateRange]);

    useEffect(() => {
        handleScrollToCell(props.selectedDays[0].toISOString(), 'auto');
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div
            className={`overflow-hidden transition-all duration-150 ease-in ${props.isHidden ? 'max-h-0' : 'max-h-64'}`}
        >
            <ScrollButtons />
            <div
                ref={rulerRef}
                className="no-scrollbar relative mx-auto flex w-full overflow-scroll bg-white md:w-[calc(100%-80px)]"
            >
                {rulerDays &&
                    rulerDays.map((month: Dayjs[], index) => (
                        <div className="first:mx-6" key={index}>
                            <span className="sticky left-6 z-[1] -translate-y-2 text-[10px] capitalize text-gray-500 sm:first:ml-6">
                                {tCa('months.full', { returnObjects: true })[month[0].month()]}
                            </span>
                            <div className="mx-2 flex sm:mx-5">
                                {month.map((day: Dayjs) => (
                                    <RulerCell
                                        isDayOff={() => checkIsDayOff(day)}
                                        onClick={() => props.onSelectDays(day)}
                                        lang={props.lang}
                                        key={day.utc().format(dates.format)}
                                        day={day}
                                        id={day.utc().format(dates.format)}
                                        isSelected={isDaySelected(day)}
                                        className={isInRangeClass(day)}
                                    />
                                ))}
                            </div>
                        </div>
                    ))}
            </div>
            <div className="flex max-w-full items-center justify-between bg-white py-6">
                <button onClick={props.todayHandler} className="border-regular rounded px-5 py-[6px] text-sm">
                    {tCa('controls.today')}
                </button>
            </div>
        </div>
    );
}
